import {
  FulfillmentTypes,
  PageEntity,
  PrinticularOrder,
  PrintServiceEntityHydrated,
  PrintServiceProductEntityHydrated,
  SettingsEntityHydrated,
  TerritoryEntityHydrated,
} from "@jackfruit/common"
import { EntityId } from "@reduxjs/toolkit"
import { Address } from "~/interfaces/entities/Address"
import { couponCodeStatus } from "~/interfaces/entities/Cart"
import { GiftCertificateEntity } from "~/interfaces/entities/GiftCertificate"
import { ImageEntity } from "~/interfaces/entities/Image"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { ImageTransformationEntity } from "~/interfaces/entities/ImageTransformation"
import { LineItemEntity } from "~/interfaces/entities/LineItem"
import { Payment } from "~/interfaces/entities/Payment"
import { ProductPageEntity } from "~/interfaces/entities/ProductPage"
import { StoreEntity } from "~/interfaces/entities/Store"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { User } from "~/interfaces/entities/User"
import { ApplicationState } from "~/redux/state/application"
import { JsonApi } from "./JsonApi"
import { convertToEntity } from "./Utils"

export interface OrderSerializerData {
  originUrl: string
  referralCode: string
  application: ApplicationState
  currency: string
  settings: SettingsEntityHydrated
  page: PageEntity
  fulfillment: FulfillmentTypes
  giftCertificates: GiftCertificateEntity[]
  store: StoreEntity
  payment: Payment
  user: User
  address: Address
  lineItems: LineItemEntity[]
  productPages: ProductPageEntity[]
  imageRegions: ImageRegionEntity[]
  images: ImageEntity[]
  imageTransformations: ImageTransformationEntity[]
  uploads: UploadEntity[]
  printService: PrintServiceEntityHydrated
  printServiceProducts: PrintServiceProductEntityHydrated[]
  territories: TerritoryEntityHydrated[]
  couponCodeStatus: couponCodeStatus
  isFree: boolean
  isPendingAddress: boolean
  meta: any
  nonce: string
}

interface AutopilotAddress extends Address {
  territoryId?: EntityId
  email?: string
  phone?: string
}

export class PrinticularSerializer {
  static serializeOrder(payload: OrderSerializerData) {
    const {
      originUrl,
      referralCode,
      application,
      currency,
      page,
      fulfillment,
      store,
      user,
      payment,
      address,
      lineItems,
      uploads,
      images,
      printService,
      printServiceProducts,
      territories,
      couponCodeStatus,
      isFree,
      isPendingAddress,
      meta,
      giftCertificates,
      nonce,
    } = payload

    const addressForAutopilot: AutopilotAddress = {
      ...address,
      email: user.email,
      phone: user.phoneNumber,
    }

    const isGiftCertificate = lineItems.some(
      (lineItem: LineItemEntity) => lineItem.giftCertificateId !== undefined
    )

    if (fulfillment === "delivery") {
      const deliveryTerritory = territories.find(
        territory => territory.code === address.country
      )
      addressForAutopilot.territoryId = deliveryTerritory?.remoteId
      addressForAutopilot.state = address.state
    }

    const printServiceForAutopilot = {
      ...printService,
      id: printService.remoteId,
    }

    const lineItemsForAutopilot = lineItems.map(lineItem => {
      const product = printServiceProducts.find(
        printServiceProduct => printServiceProduct.id === lineItem.productId
      )

      const imageIds = lineItem.renderedUploadIds.map(uploadId => {
        const upload = uploads.find(u => u.id === uploadId)
        const image = images.find(i => i.id === upload?.imageId)
        return image?.remoteId
      })

      //Gift card products require a dedicated data format.
      const data: any = isGiftCertificate
        ? {
            id: lineItem.id,
            quantity: lineItem.quantity,
            productId: product?.remoteId,
            giftCertificate: {
              to_name: giftCertificates[0].name,
              to_email_address: giftCertificates[0].email,
              message: giftCertificates[0].message,
            },
          }
        : {
            id: lineItem.id,
            quantity: lineItem.quantity,
            productId: product?.remoteId,
            vertical: lineItem.orientation === "portrait",
            productTemplateId: lineItem.productTemplateId ?? null,
            productTemplateVariantId: lineItem.productTemplateVariantId ?? null,
          }

      //imageId is an unexpected field for gift cards
      if (!isGiftCertificate) {
        if (imageIds.length === 1) {
          data.imageId = imageIds[0]
        } else {
          data.imageIds = imageIds
        }
      }

      return data
    })

    const order: any = {
      meta,
      data: {
        type: "orders",
        attributes: {
          client_name: application.name,
          client_version: application.appVersion,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          referral_code: referralCode,
          currency,
          nonce,
          app_url: originUrl,
          meta_data: {
            web: {
              origin: originUrl,
              appVersion: application.appVersion,
              appId: application.id,
              pageId: page.id,
              pageSlug: page.slug,
            },
          },
        },
        relationships: {
          print_service: {
            data: JsonApi.objectToMinimalJsonApi(
              "print_services",
              printServiceForAutopilot
            ),
          },
          line_items: {
            data: JsonApi.arrayToJsonApi("line_items", lineItemsForAutopilot),
          },
          address: {
            data: JsonApi.objectToJsonApi(
              "addresses",
              !isPendingAddress ? addressForAutopilot : {}
            ),
          },
        },
      },
    }

    if (fulfillment === "pickup") {
      order.data.relationships.store = {
        data: JsonApi.objectToMinimalJsonApi("stores", store),
      }
    }

    // Define payment method
    let paymentConfig = {}

    //Add payment type check
    const couponIsAccepted = couponCodeStatus === "accepted"
    if (couponIsAccepted && isFree) {
      // Leave it empty, no payment required
    } else {
      switch (payment.type) {
        case "stripe":
          paymentConfig = {
            stripe_token: payment.data?.cardToken,
            payment: {
              token: payment.data?.cardToken,
            },
          }
          break
        case "simplyBlu":
          paymentConfig = {
            payment: {
              simplyBlu: {
                token: payment.data?.cardToken,
              },
            },
          }
          break
        case "payInStore":
          // Leave it empty, no configuration for that method
          break
      }
    }
    order.data.attributes = {
      ...order.data.attributes,
      ...paymentConfig,
    }

    return order
  }

  static deserializeOrder(printicularOrder: any) {
    const orderEntity = convertToEntity<any>(printicularOrder.data)
    const deserializedOrder: PrinticularOrder = {
      ...orderEntity,
    } as PrinticularOrder

    const lineItemIds: number[] =
      printicularOrder.data.relationships.line_items.data.map(
        (lineItem: any) => lineItem.id
      )

    const lineItems = lineItemIds.map(id =>
      printicularOrder.included.find(
        (included: any) => included.type === "line_items" && included.id === id
      )
    )

    const deserializedLineItems = lineItems.map(lineItem => {
      const productId = lineItem.relationships.product.data.id
      const product = printicularOrder.included.find(
        (included: any) =>
          included.type === "products" && included.id === productId
      )

      const entities = convertToEntity<any>(lineItem)

      if (product) {
        const deserializedProduct = convertToEntity(product)
        entities.product = deserializedProduct
      }

      return entities
    })

    deserializedOrder.lineItems = deserializedLineItems

    return deserializedOrder
  }
}
